import type { Transcript } from '@consigli/types';

/** Source of segments built from the Teams transcript text, without the meeting audio. */
export const TEXT_ONLY_SEGMENT_SOURCE = 'teams_transcript';

/** What the backend says when it has no transcript for the meeting. */
export const TRANSCRIPT_MESSAGE_KEYS = {
  /** Teams has not published it yet: wait. */
  noTranscriptsYet: 'transcript.no_transcripts_yet',
  /** Teams cannot give it: the last resort is an upload. */
  uploadRequired: 'transcript.upload_required',
} as const;

/** Source the backend gives a transcript saved from an uploaded file, not fetched from Teams. */
export const UPLOADED_TRANSCRIPT_SOURCE = 'other';

/** True when a transcript was saved from an uploaded file. */
export const isUploadedTranscripts = (transcripts: Pick<Transcript, 'source'>[]): boolean =>
  transcripts.some((transcript) => transcript.source === UPLOADED_TRANSCRIPT_SOURCE);

/** True when there are segments and none of them was built from the audio. */
export const isTextOnlyTranscripts = (transcripts: Pick<Transcript, 'segments'>[]): boolean => {
  const segments = transcripts.flatMap((transcript) => transcript.segments ?? []);
  return segments.length > 0 && segments.every((s) => s.source === TEXT_ONLY_SEGMENT_SOURCE);
};

export type MeetingRole = 'organizer' | 'participant';

export type TranscriptBannerState =
  | 'full-quality'
  | 'limited-organizer'
  | 'limited-upload'
  | 'limited-no-audio'
  | 'limited-teams'
  | 'limited-participant'
  | 'waiting'
  | 'upload-required';

interface TranscriptBannerInput {
  transcripts: Pick<Transcript, 'segments' | 'source'>[];
  /** The backend's reason for having no transcript. */
  messageKey: string | null | undefined;
  /** Teams answered that it is still processing the transcript. */
  isProcessing: boolean;
  role: MeetingRole | null;
  /** The backend's word that the organizer can still replace the text with the audio. */
  canRegenerateWithAudio: boolean;
  /** The backend's word that no audio can replace the text, for anyone on the meeting. */
  audioUnavailable: boolean;
}

/** Which one status banner the meeting shows above its tabs, if any. */
export const getTranscriptBannerState = ({
  transcripts,
  messageKey,
  isProcessing,
  role,
  canRegenerateWithAudio,
  audioUnavailable,
}: TranscriptBannerInput): TranscriptBannerState | null => {
  const hasSegments = transcripts.some((transcript) => (transcript.segments ?? []).length > 0);

  if (hasSegments) {
    if (!isTextOnlyTranscripts(transcripts)) return 'full-quality';
    // An organizer who can still re-run is offered it, even for text a participant uploaded.
    if (canRegenerateWithAudio) return 'limited-organizer';
    // Says where the text came from, ahead of why there is no audio: both share one body.
    if (isUploadedTranscripts(transcripts)) return 'limited-upload';
    if (audioUnavailable) return 'limited-no-audio';
    // Some responses leave out the re-run flag, so an organizer can land here: telling them to
    // contact the organizer would be wrong.
    return role === 'organizer' ? 'limited-teams' : 'limited-participant';
  }

  if (isProcessing || messageKey === TRANSCRIPT_MESSAGE_KEYS.noTranscriptsYet) return 'waiting';
  if (messageKey === TRANSCRIPT_MESSAGE_KEYS.uploadRequired) return 'upload-required';
  return null;
};

/**
 * The viewer's role on the meeting, read the way the backend reads it: the meeting's role row
 * for their email. Null when they have none.
 */
export const getMeetingRole = (
  participants: { email: string; role: string }[] | null | undefined,
  email: string | null | undefined,
): MeetingRole | null => {
  const wanted = (email ?? '').trim().toLowerCase();
  if (!wanted) return null;
  const row = (participants ?? []).find((p) => p.email.trim().toLowerCase() === wanted);
  if (row?.role === 'organizer' || row?.role === 'participant') return row.role;
  return null;
};
